import { db } from "../../firebase";
import {
  collection, doc, getDocs, getDoc, setDoc, updateDoc, deleteDoc, query, where, orderBy, serverTimestamp, limit as firestoreLimit, startAfter
} from "firebase/firestore";
import {
  CREATE_PASSAGE_REQUEST,
  CREATE_PASSAGE_SUCCESS,
  CREATE_PASSAGE_FAILURE,
  FETCH_PASSAGES_REQUEST,
  FETCH_PASSAGES_SUCCESS,
  FETCH_PASSAGES_FAILURE,
  UPDATE_PASSAGE_REQUEST,
  UPDATE_PASSAGE_SUCCESS,
  UPDATE_PASSAGE_FAILURE,
  DELETE_PASSAGE_REQUEST,
  DELETE_PASSAGE_SUCCESS,
  DELETE_PASSAGE_FAILURE
} from './PassageTypes';
import { v4 as uuidv4 } from 'uuid';
import { deleteImage } from '../../util/imageUpload';
import { cleanDraftEntityMap, extractImageUrls } from '../../util/draftEntityCleanup';

/**
 * Fetch passages (optionally filter by subjectId and/or unitId)
 * Supports pagination via pageSize/lastDoc. When no pageSize is given, fetches all (backwards-compatible).
 */
export const fetchPassages = ({ subjectId = null, unitId = null, pageSize = null, lastDoc = null, append = false } = {}) => {
  return async (dispatch, getState) => {
    try {
      dispatch({ type: FETCH_PASSAGES_REQUEST });

      const passagesRef = collection(db, "questionBankPassages");
      const constraints = [orderBy("createdAt", "desc")];

      if (subjectId) constraints.unshift(where("subjectId", "==", subjectId));
      if (unitId) constraints.unshift(where("unitId", "==", unitId));

      if (pageSize) constraints.push(firestoreLimit(pageSize));
      if (lastDoc) constraints.push(startAfter(lastDoc));

      const q = query(passagesRef, ...constraints);
      const snapshot = await getDocs(q);

      const passages = snapshot.docs.map(doc => ({
        passageId: doc.id,
        ...doc.data()
      }));

      if (append) {
        const existing = getState().questionBankPassages.passages || [];
        dispatch({ type: FETCH_PASSAGES_SUCCESS, payload: [...existing, ...passages] });
      } else {
        dispatch({ type: FETCH_PASSAGES_SUCCESS, payload: passages });
      }

      const lastVisible = snapshot.docs[snapshot.docs.length - 1] || null;
      return { success: true, passages, lastVisible, hasMore: pageSize ? snapshot.docs.length === pageSize : false };
    } catch (error) {
      const errorMessage = error.message || "Failed to fetch passages";

      dispatch({
        type: FETCH_PASSAGES_FAILURE,
        payload: errorMessage
      });

      return { success: false, error: errorMessage };
    }
  };
};

/**
 * Create a new passage
 */
export const createPassage = (passageData, userEmail) => {
  return async (dispatch) => {
    try {
      dispatch({ type: CREATE_PASSAGE_REQUEST });

      // Clean Draft.js entityMap before saving
      if (passageData.passageText) {
        passageData.passageText = cleanDraftEntityMap(passageData.passageText);
      }

      const passageId = uuidv4();
      const passagesRef = collection(db, "questionBankPassages");
      const passageDocRef = doc(passagesRef, passageId);

      const newPassage = {
        passageId,
        ...passageData,
        createdAt: serverTimestamp(),
        createdBy: userEmail,
        updatedAt: serverTimestamp(),
        updatedBy: userEmail,
        questionCount: 0 // Track how many questions use this passage
      };

      await setDoc(passageDocRef, newPassage);

      dispatch({
        type: CREATE_PASSAGE_SUCCESS,
        payload: newPassage
      });

      return { success: true, passage: newPassage };
    } catch (error) {
      const errorMessage = error.message || "Failed to create passage";

      dispatch({
        type: CREATE_PASSAGE_FAILURE,
        payload: errorMessage
      });

      return { success: false, error: errorMessage };
    }
  };
};

/**
 * Update an existing passage
 */
export const updatePassage = (passageId, passageData, userEmail) => {
  return async (dispatch) => {
    try {
      dispatch({ type: UPDATE_PASSAGE_REQUEST });

      const passagesRef = collection(db, "questionBankPassages");
      const q = query(passagesRef, where("passageId", "==", passageId));
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        throw new Error("Passage not found");
      }

      const docRef = doc(db, "questionBankPassages", snapshot.docs[0].id);

      // Collect old image URLs and clean entityMap
      const currentData = snapshot.docs[0].data();
      const oldImageUrls = new Set(extractImageUrls(currentData.passageText));

      if (passageData.passageText) {
        passageData.passageText = cleanDraftEntityMap(passageData.passageText);
      }

      const newImageUrls = new Set(extractImageUrls(passageData.passageText));

      // Delete orphaned images (fire-and-forget)
      const orphanedUrls = [...oldImageUrls].filter(url => !newImageUrls.has(url));
      if (orphanedUrls.length > 0) {
        Promise.all(orphanedUrls.map(url => deleteImage(url)))
          .catch(err => console.warn('Non-critical: failed to clean up orphaned passage images', err));
      }

      const updatedPassage = {
        ...passageData,
        passageId,
        updatedAt: serverTimestamp(),
        updatedBy: userEmail
      };

      await updateDoc(docRef, updatedPassage);

      dispatch({
        type: UPDATE_PASSAGE_SUCCESS,
        payload: updatedPassage
      });

      return { success: true, passage: updatedPassage };
    } catch (error) {
      const errorMessage = error.message || "Failed to update passage";

      dispatch({
        type: UPDATE_PASSAGE_FAILURE,
        payload: errorMessage
      });

      return { success: false, error: errorMessage };
    }
  };
};

/**
 * Delete a passage
 */
export const deletePassage = (passageId) => {
  return async (dispatch) => {
    try {
      dispatch({ type: DELETE_PASSAGE_REQUEST });

      // First check if any active (non-archived) questions are using this passage
      const questionsRef = collection(db, "questionBankQuestions");
      const questionsQuery = query(questionsRef, where("passageId", "==", passageId));
      const questionsSnapshot = await getDocs(questionsQuery);
      const activeQuestions = questionsSnapshot.docs.filter(d => d.data().status !== 'archived');

      if (activeQuestions.length > 0) {
        throw new Error(`Cannot delete passage. ${activeQuestions.length} active question(s) are attached to this passage. Archive the questions first.`);
      }

      // Delete the passage
      const passagesRef = collection(db, "questionBankPassages");
      const q = query(passagesRef, where("passageId", "==", passageId));
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        throw new Error("Passage not found");
      }

      const docRef = doc(db, "questionBankPassages", snapshot.docs[0].id);
      await deleteDoc(docRef);

      dispatch({
        type: DELETE_PASSAGE_SUCCESS,
        payload: passageId
      });

      return { success: true };
    } catch (error) {
      const errorMessage = error.message || "Failed to delete passage";

      dispatch({
        type: DELETE_PASSAGE_FAILURE,
        payload: errorMessage
      });

      return { success: false, error: errorMessage };
    }
  };
};

/**
 * #25: Sync passage questionCount with actual linked questions count.
 * Fixes drift where questionCount gets out of sync due to partial failures.
 */
export const syncPassageQuestionCounts = () => {
  return async () => {
    try {
      // Fetch all questions with passageId
      const questionsRef = collection(db, "questionBankQuestions");
      const questionsSnapshot = await getDocs(questionsRef);

      // Count active (non-archived) questions per passageId
      const countsByPassageId = {};
      questionsSnapshot.docs.forEach(docSnap => {
        const data = docSnap.data();
        if (data.passageId && data.status !== 'archived') {
          countsByPassageId[data.passageId] = (countsByPassageId[data.passageId] || 0) + 1;
        }
      });

      // Fetch all passages and update any with incorrect counts
      const passagesRef = collection(db, "questionBankPassages");
      const passagesSnapshot = await getDocs(passagesRef);
      let updatedCount = 0;

      for (const passageDoc of passagesSnapshot.docs) {
        const data = passageDoc.data();
        const passageId = data.passageId || passageDoc.id;
        const actualCount = countsByPassageId[passageId] || 0;
        const storedCount = data.questionCount || 0;

        if (actualCount !== storedCount) {
          const passageDocRef = doc(db, "questionBankPassages", passageDoc.id);
          await updateDoc(passageDocRef, { questionCount: actualCount });
          updatedCount++;
        }
      }

      return { success: true, updatedCount };
    } catch (error) {
      console.error("Failed to sync passage question counts:", error);
      return { success: false, error: error.message };
    }
  };
};

/**
 * Fetch a single passage by ID
 */
export const fetchPassageById = (passageId) => {
  return async (dispatch) => {
    try {
      const passagesRef = collection(db, "questionBankPassages");
      const q = query(passagesRef, where("passageId", "==", passageId));
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        throw new Error("Passage not found");
      }

      const passage = {
        passageId: snapshot.docs[0].id,
        ...snapshot.docs[0].data()
      };

      return { success: true, passage };
    } catch (error) {
      const errorMessage = error.message || "Failed to fetch passage";
      return { success: false, error: errorMessage };
    }
  };
};
